import React, { useEffect, useState } from 'react';
import axios from 'axios';
// Chart.js and react-chartjs-2 imports
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Legend,
  Tooltip,
} from 'chart.js';

ChartJS.register(LineElement, PointElement, CategoryScale, LinearScale, Legend, Tooltip);

// Helper to get first and last day of current month in YYYY-MM-DD format
const getMonthRange = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const toISO = d => d.toISOString().slice(0, 10);
  return [toISO(firstDay), toISO(lastDay)];
};

const SalesByOrderType = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  // Set default startDate and endDate to current month
  const [startDate, setStartDate] = useState(getMonthRange()[0]);
  const [endDate, setEndDate] = useState(getMonthRange()[1]);
  const [granularity, setGranularity] = useState('month'); // default to month
  const [view, setView] = useState('table'); // 'table' or 'graph'

  // Helper to move to previous or next month
  const changeMonth = (direction) => {
    // Parse startDate as local date (YYYY-MM-DD)
    const [yearStr, monthStr] = startDate.split('-');
    let year = parseInt(yearStr, 10);
    let month = parseInt(monthStr, 10) - 1; // JS months are 0-based

    if (direction === 'prev') {
      month -= 1;
      if (month < 0) {
        month = 11;
        year -= 1;
      }
    } else if (direction === 'next') {
      month += 1;
      if (month > 11) {
        month = 0;
        year += 1;
      }
    }
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const toISO = d => d.toISOString().slice(0, 10);
    setStartDate(toISO(firstDay));
    setEndDate(toISO(lastDay));
  };

  const fetchData = () => {
    setLoading(true);
    setError('');
    axios.get('/api/reports/sales-by-order-type', {
      params: {
        startDate,
        endDate,
        granularity,
      },
    })
      .then(res => {
        setData(res.data);
        setLoading(false);
      })
      .catch(() => {
        setError('Failed to fetch sales by order type');
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line
  }, [startDate, endDate, granularity]);

  const handleSubmit = (e) => {
    e.preventDefault();
    fetchData();
  };

  // CSV download functionality
  const downloadCSV = () => {
    if (!data.length) return;
    const headers = ['Order Type', 'Period', 'Total Sales'];
    const rows = data.map(row => [row.orderType, row.period, row.totalSales]);
    const csvContent =
      [headers, ...rows]
        .map(e => e.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))
        .join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'sales_by_order_type.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Prepare data for the line chart
  const getChartData = () => {
    // Use periodEndDate as the label for the X axis
    const periodEndDates = Array.from(new Set(data.map(row => row.periodEndDate))).sort();
    // Get unique order types
    const orderTypes = Array.from(new Set(data.map(row => row.orderType)));
    // Build datasets
    const datasets = orderTypes.map((orderType, idx) => ({
      label: orderType,
      data: periodEndDates.map(periodEndDate => {
        const found = data.find(row => row.orderType === orderType && row.periodEndDate === periodEndDate);
        return found ? Number(found.totalSales) : 0;
      }),
      fill: false,
      borderColor: `hsl(${(idx * 60) % 360}, 70%, 50%)`,
      backgroundColor: `hsl(${(idx * 60) % 360}, 70%, 50%)`,
      tension: 0.2,
    }));

    return {
      labels: periodEndDates,
      datasets,
    };
  };

  return (
    <div>
      <h2>Sales by Order Type</h2>
      <form onSubmit={handleSubmit} style={{ marginBottom: '1rem' }}>
        <button type="button" onClick={() => changeMonth('prev')}>Previous Month</button>
        &nbsp;
        <button type="button" onClick={() => changeMonth('next')}>Next Month</button>
        &nbsp;&nbsp;
        <label>
          Start Date:&nbsp;
          <input
            type="date"
            value={startDate}
            onChange={e => setStartDate(e.target.value)}
          />
        </label>
        &nbsp;&nbsp;
        <label>
          End Date:&nbsp;
          <input
            type="date"
            value={endDate}
            onChange={e => setEndDate(e.target.value)}
          />
        </label>
        &nbsp;&nbsp;
        <label>
          Granularity:&nbsp;
          <select
            value={granularity}
            onChange={e => setGranularity(e.target.value)}
          >
            <option value="day">Day</option>
            <option value="week">Week</option>
            <option value="month">Month</option>
          </select>
        </label>
        &nbsp;&nbsp;
        <button type="submit">Apply</button>
        &nbsp;&nbsp;
        <button type="button" onClick={downloadCSV} disabled={!data.length}>
          Download CSV
        </button>
        &nbsp;&nbsp;
        <button
          type="button"
          onClick={() => setView(view === 'table' ? 'graph' : 'table')}
          disabled={loading || !!error}
        >
          {view === 'table' ? 'Show Graph' : 'Show Table'}
        </button>
      </form>
      {loading && <p>Loading...</p>}
      {error && <p style={{ color: 'red' }}>{error}</p>}
      {!loading && !error && view === 'table' && (
        <table border="1" cellPadding="8" style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th>Order Type</th>
              <th>Period</th>
              <th>Period End Date</th>
              <th>Total Sales</th>
            </tr>
          </thead>
          <tbody>
            {data.map((row, idx) => (
              <tr key={idx}>
                <td>{row.orderType}</td>
                <td>{row.period}</td>
                <td>{row.periodEndDate}</td>
                <td>{row.totalSales}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {!loading && !error && view === 'graph' && (
        <div style={{ background: '#fff', padding: '1rem', borderRadius: 8 }}>
          <Line
            data={getChartData()}
            options={{
              responsive: true,
              plugins: {
                legend: { position: 'top' },
                title: { display: true, text: 'Sales by Order Type Over Time' },
              },
              scales: {
                y: { beginAtZero: true, title: { display: true, text: 'Total Sales ($)' } },
                x: { title: { display: true, text: 'Period End Date' } },
              },
            }}
            height={400}
          />
        </div>
      )}
    </div>
  );
};

export default SalesByOrderType;